 "use client";
import { EKYC_MESSAGES, EKYC_STATUS } from "@/lib/utils/ekyc.utils";
import {
  LoaderComponent,
  PaperComponent,
  SnackbarComponent,
} from "@/components";
import { useState, useEffect, useCallback, useMemo, useRef, use } from "react";
import { useSearchParams, useRouter } from "next/navigation";
import { useFormik } from "formik";
import { useApplication } from "@/lib/hooks/application.hooks";
import {
  useInitiateEkycMutation,
  useEkycConsentMutation,
  useLazyEkycStatusQuery,
  useSendBeaconEkycPerformedMutation,
  useConsentCreateMutation,
  useGetApplicationStageConfigQuery,
} from "@/lib/redux";
import { useFooter } from "@/lib/context/FooterContext";
import { sessionStorageUtil } from "@/lib/utils/common.utils";
import {
  APP_KEYS,
  ConsentType,
  getConsentPayload,
  STAGE,
  STAGE_BASED_ROUTING,
  SUBSTAGE,
  WEB_EVENTS,
} from "@/lib/constants";
import * as Yup from "yup";
import { usePolling } from "@/lib/hooks/common.hooks";
import { ERROR_TYPES } from "@/lib/utils/errorhandling.utils";
import useErrorHandling from "@/lib/hooks/errorhandling";
import EkycVerification from "./EkycVerification";
import { useTranslations } from "next-intl";
import { useDispatchModalAttributes } from "@/components/Modals/RejectedLoanModal";
import { useEvents } from "@/lib/hooks/events.hooks";
const EKYC_SCHEMA = Yup.object().shape({
  addressConsent: Yup.boolean().oneOf(
    [true],
    EKYC_MESSAGES.ADDRESS_CONSENT_REQUIRED,
  ),
  consent: Yup.boolean().oneOf(
    [true],
    EKYC_MESSAGES.EKYC_CONSENT_REQUIRED,
  ),
});

const POOLING_INTERVAL = 5000; // 5 seconds
const POOLING_TIME = 60000; // 1 minute

/**
 * EKYC_PENDING -> initiatesAPI -> kycurl -> redirect
 * On landing -> /complete api with reqId -> substage to EKYC_PENDING changed -> call the status api pooling
 * FAILURE / SUCCESS -> close loader,
 *
 *
 *
 */


export default function EkycVerificationScreen({ product }) {
  const [isLoading, setIsLoading] = useState(false);
  const { setActions } = useFooter();
  const searchParams = useSearchParams();
  const router = useRouter();
  const redirectSource = searchParams.get("redirectSource");
  const requestId = searchParams.get("requestId");
  const hasProcessedEkycRedirect = useRef(false);

  const t = useTranslations("ekycVerification");

  const dispatchModalAttributes = useDispatchModalAttributes();
  const { sendEventOnce } = useEvents();

  const [
    initiateEkyc,
    { isLoading: isInitiateEkycLoading, data, error: initiateEkycError },
  ] = useInitiateEkycMutation();
  const [
    sendBeaconEkycPerformed,
    {
      isLoading: isSendBeaconEkycPerformedLoading,
      data: sendBeaconEkycPerformedData,
      error: sendBeaconEkycPerformedError,
    },
  ] = useSendBeaconEkycPerformedMutation();

  const [
    ekycConsent,
    {
      isLoading: isEkycConsentLoading,
      data: ekycConsentData,
      error: ekycConsentError,
      isSuccess: isEkycConsentSuccess,
    },
  ] = useEkycConsentMutation();

  const [
    createConsent,
    {
      data: consentData,
      isLoading: consentDataLoading,
      error: errorCreateConsent,
      isSuccess: consentCreated,
    },
  ] = useConsentCreateMutation();

  const {
    productApplication,
    isLoading: isLoadingApplication,
    error: applicationError,
    refetch: refetchApplication,
  } = useApplication("", {
    refetchOnMountOrArgChange: true,
    refetchOnFocus: true,
  });

  const [
    getEkycStatus,
    {
      data: ekycStatusData,
      isFetching: isEkycStatusFetching,
      error: ekycStatusError,
      isSuccess: isEkycStatusSuccess,
    },
  ] = useLazyEkycStatusQuery();


  const {
    data: stageConfigsData,
    isLoading: isLoadingAppStageConfig,
    error: appStageConfigError,
    refetch: refetchAppStageConfig,
  } = useGetApplicationStageConfigQuery(
    {
      appId: productApplication?.appId,
      stage: productApplication?.stage,
    },
    {
      skip: !productApplication?.appId || !productApplication?.stage,
      refetchOnMountOrArgChange: true,
    },
  );

  const noPollingSubStages = useMemo(
    () => stageConfigsData?.data?.noPollingSubStages,
    [stageConfigsData],
  );


  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      addressConsent: false,
      consent: false,
    },
    validationSchema: EKYC_SCHEMA,
  });

  const initiateEkycFn = useCallback(async () => {
    setIsLoading(true);
    try {
      const consentPayload = getConsentPayload(
        ConsentType.EKYC,
        productApplication,
        Boolean(formik?.values?.consent),
      );
      await createConsent(consentPayload)?.unwrap();
      const response = await initiateEkyc({
        appId: productApplication?.appId,
      })?.unwrap();

      const { attemptCount, kycUrl, txnId, reqId } = response?.data;

      if (kycUrl) {
        sessionStorageUtil.set(APP_KEYS.PRODUCT_TYPE, product);
        window.location.assign(kycUrl);
      }

      // handle attempt error as well here to
    } catch (error) {
          setIsLoading(false);
      if(error?.data?.detail?.name === SUBSTAGE.EKYC_MAX_ATTEMPTS_REACHED){
        dispatchModalAttributes({
          open: true,
          message: {
            label:EKYC_MESSAGES.MAX_ATTEMPT_TITLE,
            subLabel: error?.data?.message
          },
          type: ERROR_TYPES.ATTEMPT_ERROR,
        });
      }
  
    }
  }, [
    initiateEkyc,
    productApplication,
    product,
    dispatchModalAttributes,
    formik?.values?.consent,
  ]);

  const handleProceedToKyc = useCallback(async () => {
    const { stage, subStage } = productApplication || {};
    const { data: ekycStatus } = ekycStatusData || {};
    if (stage === STAGE.EKYC && subStage === SUBSTAGE.EKYC_PENDING) {
      // initiate ekyc api call
      initiateEkycFn();
    } else if (
      stage === STAGE.EKYC &&
      subStage && noPollingSubStages?.length > 0 &&
      !noPollingSubStages?.includes(subStage)
    ) {
      if (!ekycStatus || ekycStatus?.retry) {
        initiateEkycFn();
      } else if (ekycStatus && !ekycStatus?.retry) {
        // dispatch modal error for showing no retry left
        dispatchModalAttributes({
          open: true,
          title: ekycStatus?.heading || EKYC_MESSAGES.EKYC_INCOMPLETE,
          message: EKYC_MESSAGES.PROCESSING_REQUEST,
        });
      }
    } else {
      initiateEkycFn();
    }
  }, [
    initiateEkycFn,
    ekycStatusData,
    productApplication,
    dispatchModalAttributes,
    noPollingSubStages,
  ]);


  const onPollingCompleted = (data, error) => {
    if (error) {
      setIsLoading(false);
      dispatchModalAttributes({
        open: true,
        title: error?.heading || EKYC_MESSAGES.EKYC_INCOMPLETE,
        message:
          error?.message || EKYC_MESSAGES.STATUS_UNKNOWN_ERROR,
      });
      return;
    }
    const { overallStatus, message, retry , heading} = data?.data || {};

    if (overallStatus === EKYC_STATUS.SUCCESS) {
      SnackbarComponent({
        success: true,
        successMessage: message,
      });
      try {
        refetchApplication();
      } catch (e) {}
    } else if (
      [EKYC_STATUS.PENDING, EKYC_STATUS.INITIATED].includes(
        overallStatus?.toUpperCase(),
      ) 
    ) {
      dispatchModalAttributes({
        open: true,
        message,
        buttonLabel: "Get latest status",
        onButtonClick: () => {
          // reset modal & start polling
          dispatchModalAttributes();
          resetPolling();
          startPolling();
        },
      });
      setIsLoading(false);
    } else {
      // errors
      dispatchModalAttributes({
        open: true,
        message,
        title: heading || EKYC_MESSAGES.EKYC_INCOMPLETE,
        buttonLabel: retry ? "Try Again" : "Ok",
      });
      setIsLoading(false);
    }
  };

  const pollFunction = useCallback(
    () => productApplication?.appId && getEkycStatus({ appId: productApplication?.appId }),
    [productApplication?.appId],
  );

  const {
    data: statusPollingData,
    resetPolling,
    startPolling,
  } = usePolling({
    pollFn: pollFunction,
    interval: POOLING_INTERVAL,
    totalPollingTime: POOLING_TIME,
    immediate: false,
    retryOnError: false,
    onStart: () => setIsLoading(true),
    stopCondition: (data) => {
      if (!data?.data) return false;
      const { overallStatus } = data?.data || {};
      return ![EKYC_STATUS.PENDING, EKYC_STATUS.INITIATED].includes(overallStatus?.toUpperCase());
    },
    onComplete: onPollingCompleted,
  });

  const errors =
    applicationError ||
    ekycStatusError ||
    ekycConsentError ||
    initiateEkycError ||
    errorCreateConsent ||
    sendBeaconEkycPerformedError;

  useErrorHandling(errors);


  /* -------------------- EFFECTS ------------------- */

  useEffect(() => {
    const { stage, subStage } = productApplication || {};
    if (stage && stage !== STAGE.EKYC) {
      router.push(STAGE_BASED_ROUTING(stage));
      return;
    }
    if (subStage && subStage === SUBSTAGE.EKYC_MAX_ATTEMPTS_REACHED) {
      setIsLoading(false);
      dispatchModalAttributes({
        open: true,
        message: {
          label: EKYC_MESSAGES.MAX_ATTEMPT_TITLE,
          subLabel: EKYC_MESSAGES.MAX_ATTEMPT_TEXT,
        },
        type: ERROR_TYPES.ATTEMPT_ERROR,
      });
    }
  }, [productApplication]);

  sendEventOnce(WEB_EVENTS.ON_EKYC_VERIFICATION_SCREEN)

  useEffect(() => {
    setActions({
      showConfirm: true,
      showReject: true,
      confirmBtnLabel: t("startAadhar"),
      disabledConfirmBtn:
        !formik.values.addressConsent || !formik.values.consent,
      onConfirm: handleProceedToKyc,
    });
  }, [
    setActions,
    handleProceedToKyc,
    formik?.errors?.addressConsent,
    formik?.errors?.consent,
  ]);


  // hook for status polling based on redirect source and SUBSTAGE.EKYC_INITIATED
  useEffect(() => {
    const postRedirectPolling = async () => {
      if (
        requestId &&
        redirectSource?.toUpperCase() === STAGE.EKYC &&
        !hasProcessedEkycRedirect.current
      ) {
        hasProcessedEkycRedirect.current = true;

        try {
          await sendBeaconEkycPerformed({
            appId: productApplication?.appId,
            requestId,
          }).unwrap();

          startPolling();
        } catch (error) {
          console.error("Failed to mark eKYC performed", error);
        }

        return;
      }

      // Existing initiated application
      if (
        productApplication?.stage === STAGE.EKYC && noPollingSubStages &&
        !noPollingSubStages?.includes(productApplication?.subStage)
      ) {
        startPolling();
      }
    };

    postRedirectPolling();
  }, [
    requestId,
    redirectSource,
    productApplication?.appId,
    productApplication?.stage,
    productApplication?.subStage,
    noPollingSubStages,
    sendBeaconEkycPerformed,
    startPolling,
  ]);

  const loading =
    isLoadingApplication ||
    isEkycConsentLoading ||
    isLoading ||
    isInitiateEkycLoading ||
    isLoadingAppStageConfig;

  return (
    <PaperComponent>
      <LoaderComponent
        open={loading}
        loadingLabel={
          [EKYC_STATUS.PENDING, EKYC_STATUS.INITIATED].includes(
            ekycStatusData?.data?.overallStatus?.toUpperCase(),
          )
            ?   EKYC_MESSAGES.CONFIRMING_DETAILS
            : undefined
        }
      />
      <EkycVerification
        addressConsent={formik.values.addressConsent}
        handleAddressConsent={(e) => {
          const checked = e.target.checked;
          ekycConsent({
            appId: productApplication?.appId,
            payload: {
              addressConsent: checked,
            },
          })
            .unwrap()
            .then((res) => {
              formik.setFieldValue("addressConsent", checked);
            });
        }}
        handleKycConsent={formik.handleChange}
        kycConsent={formik.values.consent}
      />
    </PaperComponent>
  );
}
